import {CheckCircleIcon, ExclamationTriangleIcon, InformationCircleIcon, XCircleIcon, XMarkIcon} from "@heroicons/react/20/solid";
import {createContext, PropsWithChildren, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {OtsBrand, OtsColorSchemePreference, OtsThemeRoot} from "../theme/OtsTheme";

export type OtsToastTone = "info" | "success" | "warning" | "danger";

export type OtsToastProps = {
  title: ReactNode;
  description?: ReactNode;
  tone?: OtsToastTone;
  /** One follow-up, e.g. a Download link or a Retry button. */
  action?: ReactNode;
  onDismiss?: () => void;
  className?: string;
};

const toneStyles: Record<OtsToastTone, {chip: string; Icon: typeof InformationCircleIcon}> = {
  info: {chip: "bg-[var(--ots-primary-container)] text-[var(--ots-primary)]", Icon: InformationCircleIcon},
  success: {chip: "bg-[var(--ots-success-container)] text-[var(--ots-success)]", Icon: CheckCircleIcon},
  warning: {chip: "bg-[var(--ots-warning-container)] text-[var(--ots-warning)]", Icon: ExclamationTriangleIcon},
  danger: {chip: "bg-[var(--ots-danger-container)] text-[var(--ots-error)]", Icon: XCircleIcon},
};

/**
 * Foundational transient notice: an outcome the person didn't wait on, with at most one follow-up. The tone shows in
 * the icon chip only, so a stack of toasts stays calm.
 */
export const OtsToast = ({title, description, tone = "info", action, onDismiss, className}: OtsToastProps) => {
  const {chip, Icon} = toneStyles[tone];

  return <div
    data-tone={tone}
    className={[
      "pointer-events-auto flex w-[22rem] max-w-[calc(100vw-2rem)] items-start gap-3 rounded-xl border",
      "border-[var(--ots-outline-variant)] bg-[var(--ots-surface)] p-3 text-[var(--ots-text)]",
      "shadow-[0_8px_24px_-12px_rgb(15_23_42/0.35),0_1px_2px_rgb(15_23_42/0.06)]",
      className,
    ].filter(Boolean).join(" ")}>
    <span aria-hidden="true" className={["grid size-8 shrink-0 place-items-center rounded-full", chip].join(" ")}>
      <Icon className="size-[18px]"/>
    </span>
    <div className="min-w-0 flex-1 pt-1">
      <p className="text-sm/5 font-semibold">{title}</p>
      {description != null && <p className="mt-0.5 text-[13px]/5 text-[var(--ots-text-muted)]">{description}</p>}
      {action != null && <div className="mt-2.5 flex items-center gap-2">{action}</div>}
    </div>
    {onDismiss && <button
      type="button"
      onClick={onDismiss}
      aria-label="Dismiss"
      className="rounded-full p-1.5 text-[var(--ots-text-muted)] hover:bg-[var(--ots-neutral-container)] hover:text-[var(--ots-text)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--ots-primary)]">
      <XMarkIcon aria-hidden="true" className="size-4"/>
    </button>}
  </div>;
};

export type OtsToastOptions = Omit<OtsToastProps, "onDismiss" | "className"> & {
  /** Replaces a toast with the same id instead of stacking a second one. */
  id?: string;
  /** Milliseconds before it goes away; 0 keeps it until dismissed. Pauses while hovered or focused. */
  duration?: number;
};

type ToastEntry = OtsToastOptions & {id: string};

export type OtsToastApi = {
  show: (toast: OtsToastOptions) => string;
  dismiss: (id: string) => void;
};

const OtsToastContext = createContext<OtsToastApi | null>(null);

/** The nearest toast provider, or null when none is mounted. */
export const useOptionalOtsToast = () => useContext(OtsToastContext);

export const useOtsToast = (): OtsToastApi => {
  const api = useContext(OtsToastContext);
  if (!api) throw new Error("useOtsToast must be used inside an OtsToastProvider");
  return api;
};

export type OtsToastProviderProps = PropsWithChildren<{
  /** Default time on screen. */
  duration?: number;
  /** Most toasts shown at once; the oldest leave first, so a burst can't cover the screen. */
  limit?: number;
  brand?: OtsBrand;
  colorScheme?: OtsColorSchemePreference;
}>;

const DEFAULT_DURATION = 8_000;
/** Toasts shown at once by default. */
export const OTS_TOAST_DEFAULT_LIMIT = 3;
const DEFAULT_LIMIT = OTS_TOAST_DEFAULT_LIMIT;

const TimedToast = ({entry, onDismiss}: {entry: ToastEntry; onDismiss: (id: string) => void}) => {
  const duration = entry.duration ?? DEFAULT_DURATION;
  const [paused, setPaused] = useState(false);
  const remaining = useRef(duration);
  const startedAt = useRef(0);

  useEffect(() => {
    if (duration <= 0 || paused) return;
    startedAt.current = Date.now();
    const timer = setTimeout(() => onDismiss(entry.id), remaining.current);
    return () => {
      clearTimeout(timer);
      remaining.current -= Date.now() - startedAt.current;
    };
  }, [duration, paused, entry.id, onDismiss]);

  return <div
    onMouseEnter={() => setPaused(true)}
    onMouseLeave={() => setPaused(false)}
    onFocus={() => setPaused(true)}
    onBlur={() => setPaused(false)}>
    <OtsToast
      title={entry.title}
      description={entry.description}
      tone={entry.tone}
      action={entry.action}
      onDismiss={() => onDismiss(entry.id)}/>
  </div>;
};

/**
 * Holds the toast stack and renders it bottom-right in a polite live region, so screen readers announce new toasts
 * without interrupting.
 */
export const OtsToastProvider = ({
  duration = DEFAULT_DURATION,
  limit = DEFAULT_LIMIT,
  brand,
  colorScheme,
  children,
}: OtsToastProviderProps) => {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const nextId = useRef(0);

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const show = useCallback((toast: OtsToastOptions) => {
    const id = toast.id ?? `ots-toast-${++nextId.current}`;
    setToasts((current) => [
      ...current.filter((existing) => existing.id !== id),
      {...toast, id, duration: toast.duration ?? duration},
    ].slice(-Math.max(1, limit)));
    return id;
  }, [duration, limit]);

  const api = useMemo(() => ({show, dismiss}), [show, dismiss]);

  return <OtsToastContext.Provider value={api}>
    {children}
    <OtsThemeRoot
      brand={brand}
      colorScheme={colorScheme}
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed bottom-4 right-4 z-[1300] flex flex-col items-end gap-2">
      {toasts.map((entry) => <TimedToast key={entry.id} entry={entry} onDismiss={dismiss}/>)}
    </OtsThemeRoot>
  </OtsToastContext.Provider>;
};
